import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Footprints, Beef, Scale, Zap, CheckCircle2, ChevronRight, Utensils } from 'lucide-react';

import { useApiData } from '../lib/hooks.js';
import { api } from '../lib/api.js';
import { useToast } from '../lib/store.js';
import { relativeDay, fmtDate, num, kg, toStone, SLOT_LABEL } from '../lib/format.js';
import { Card, Button, Sheet, Field, NumberInput, TextArea, Choice, Spinner, ErrorNote, Meter, Pill, SectionTitle } from '../components/ui.jsx';
import { MigraineSheet } from './Migraines.jsx';

// The home screen answers one question: what is worth doing today? The brief
// says the hard part is starting, so the session comes first, at full width,
// with the short version offered in the same breath.

export default function Today() {
  const { data, loading, error, reload } = useApiData('/today');
  const [sheet, setSheet] = useState(null);
  const navigate = useNavigate();

  if (loading && !data) return <Spinner label="Good morning" />;
  if (error) return <div className="p-gutter"><ErrorNote error={error} onRetry={reload} /></div>;
  if (!data) return null;

  const { programme, today, targets, trends, guidance, suggestions } = data;
  const daily = today.daily || {};

  return (
    <div className="px-gutter pt-6 max-w-lg mx-auto">
      <header className="mb-5">
        <p className="text-label uppercase text-textMute mb-1">{relativeDay(data.date)} · {fmtDate(data.date, 'long')}</p>
        <h1 className="text-page-title">
          Week {programme.week} of {programme.totalWeeks}
          {programme.deload && <span className="ml-2 align-middle"><Pill tone="accent">Easier week</Pill></span>}
        </h1>
        <p className="text-body-sm text-textSoft mt-1">
          {programme.phase.name} · {programme.daysToTarget > 0 ? `${num(programme.daysToTarget)} days to ${fmtDate(data.profile.target_date, 'short')}` : 'Target date reached'}
        </p>
      </header>

      <GuidanceCard guidance={guidance} onGo={(to) => navigate(to)} />

      {suggestions.length > 0 && <SuggestionCard suggestions={suggestions} onDone={reload} />}

      {targets.programme_mode !== 'health' && (
        <Card className="mb-4">
          <SectionTitle>Today</SectionTitle>
          <div className="space-y-3.5">
            <Meter
              label="Steps"
              value={daily.steps}
              target={targets.step_target}
              valueLabel={daily.steps != null ? `${num(daily.steps)} / ${num(targets.step_target)}` : 'Not logged'}
              tone="s2"
            />
            <Meter
              label="Protein"
              value={daily.protein_g}
              target={targets.protein_g}
              valueLabel={daily.protein_g != null ? `${daily.protein_g}g / ${targets.protein_g}g` : 'Not logged'}
              tone="accent"
            />
            <div className="flex items-baseline justify-between">
              <span className="text-body-sm text-textSoft">Eating</span>
              <span className="text-body-sm font-semibold">
                {daily.kcal != null
                  ? `${num(daily.kcal)} kcal`
                  : daily.kcal_band
                    ? { under: 'Under the range', in: 'In the range', over: 'Over the range' }[daily.kcal_band]
                    : `Aiming for ${num(targets.kcal_low)}–${num(targets.kcal_high)}`}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2 mt-5">
            <QuickButton icon={Footprints} label="Steps" onClick={() => setSheet('steps')} done={daily.steps != null} />
            <QuickButton icon={Beef} label="Protein" onClick={() => setSheet('protein')} done={daily.protein_g != null} />
            <QuickButton icon={Scale} label="Weight" onClick={() => setSheet('weight')} />
            <QuickButton icon={Zap} label="How I feel" onClick={() => setSheet('feel')} done={daily.energy != null} />
          </div>
        </Card>
      )}

      {today.meals.length > 0 && (
        <Card className="mb-4">
          <SectionTitle action={<Link to="/food" className="text-body-sm text-accent">Full week</Link>}>Meals today</SectionTitle>
          <ul className="divide-y divide-edge -my-1">
            {today.meals.map((m) => (
              <li key={m.id} className="py-2.5 flex items-center gap-3">
                <span className="text-label uppercase text-textMute w-16 shrink-0">{SLOT_LABEL[m.slot]}</span>
                {/* Mirrors the food page: once she has eaten out or skipped,
                    the outcome is what gets shown, not the original plan. */}
                <span className="flex-1 min-w-0 text-body-sm">
                  {m.status === 'out' ? (
                    <span className="text-textSoft italic">{m.note || 'Ate out'}</span>
                  ) : m.status === 'skipped' ? (
                    <span className="text-textSoft italic">Skipped</span>
                  ) : (
                    m.name || 'Nothing planned'
                  )}
                </span>
                {m.status === 'eaten' && <CheckCircle2 size={15} className="text-good shrink-0" />}
                {m.status !== 'out' && m.status !== 'skipped' && m.protein_g != null && (
                  <span className="text-meta text-textMute shrink-0">{m.protein_g}g</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {today.meals.length === 0 && (
        <Card className="mb-4">
          <div className="flex items-center gap-3">
            <Utensils size={20} className="text-textMute shrink-0" />
            <p className="text-body-sm text-textSoft flex-1">No meal plan for this week yet.</p>
            <Link to="/food" className="text-body-sm text-accent font-semibold shrink-0">Make one</Link>
          </div>
        </Card>
      )}

      <Card className="mb-4">
        <SectionTitle action={<Link to="/progress" className="text-body-sm text-accent">Progress</Link>}>Where things stand</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-label uppercase text-textMute mb-1">Latest weight</p>
            <p className="text-lg font-semibold">{trends.latestWeight ? kg(trends.latestWeight.kg) : '—'}</p>
            {trends.latestWeight && <p className="text-meta text-textMute">{toStone(trends.latestWeight.kg)}</p>}
          </div>
          <div>
            <p className="text-label uppercase text-textMute mb-1">Steps, 2 weeks</p>
            <p className="text-lg font-semibold">{trends.avgSteps14 != null ? num(trends.avgSteps14) : '—'}</p>
            <p className="text-meta text-textMute">daily average</p>
          </div>
        </div>
        {trends.weight.direction !== 'unknown' && (
          <p className="text-meta text-textMute mt-3 pt-3 border-t border-edge">
            {trends.weight.direction === 'down'
              ? `Trending down about ${Math.abs(trends.weight.changePerWeek).toFixed(2)}kg a week over the last month.`
              : trends.weight.direction === 'up'
                ? 'Trending slightly up over the last month. Worth a look at the weekly review rather than a reaction.'
                : 'Holding steady over the last month.'}
          </p>
        )}
      </Card>

      <WeekStrip programme={programme} today={data.date} />

      <div className="mt-4 mb-6">
        <Link to="/review" className="flex items-center justify-between card hover:border-edgeHi transition-colors">
          <span className="text-body-sm font-semibold">This week&rsquo;s review</span>
          <ChevronRight size={18} className="text-textMute" />
        </Link>
      </div>

      <LogSheets
        sheet={sheet}
        onClose={() => setSheet(null)}
        date={data.date}
        daily={daily}
        onSaved={reload}
        targets={targets}
        onMigraine={() => setSheet('migraine')}
      />
      <MigraineSheet
        open={sheet === 'migraine'}
        date={data.date}
        onClose={() => setSheet(null)}
        onSaved={() => { setSheet(null); reload(); }}
      />
    </div>
  );
}

function GuidanceCard({ guidance, onGo }) {
  const toneRing = {
    action: 'border-accent bg-accentSoft',
    good: 'border-good/40 bg-goodSoft',
    care: 'border-warn/40 bg-warnSoft',
    calm: '',
  }[guidance.tone] || '';

  return (
    <Card className={`mb-4 ${toneRing}`}>
      <h2 className="text-card-title font-display mb-1.5">{guidance.headline}</h2>
      <p className="text-body-sm text-textSoft">{guidance.body}</p>
      {guidance.cta && (
        <Button className="w-full mt-4" onClick={() => onGo(guidance.cta.to)}>
          {guidance.cta.label}
        </Button>
      )}
    </Card>
  );
}

function SuggestionCard({ suggestions, onDone }) {
  const { show } = useToast();
  const [busy, setBusy] = useState(false);
  const s = suggestions[0];

  async function respond(decision) {
    setBusy(true);
    try {
      await api.post('/suggestions', { id: s.id, decision });
      show(decision === 'accept' ? 'Done — your targets have been updated.' : 'No problem, leaving things as they are.');
      onDone();
    } catch (err) {
      show(err.message, 'crit');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-4 border-accent/40">
      <Pill tone="accent">A suggestion</Pill>
      <h2 className="text-card-title font-display mt-2.5 mb-1.5">{s.title}</h2>
      <p className="text-body-sm text-textSoft mb-3">{s.body}</p>
      {s.rationale && <p className="text-meta text-textMute mb-4 pl-3 border-l-2 border-edge">{s.rationale}</p>}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => respond('accept')} disabled={busy} className="flex-1">Yes, do that</Button>
        <Button size="sm" variant="secondary" onClick={() => respond('decline')} disabled={busy} className="flex-1">No thanks</Button>
      </div>
      {suggestions.length > 1 && <p className="text-meta text-textMute mt-3">{suggestions.length - 1} more after this one.</p>}
    </Card>
  );
}

function QuickButton({ icon: Icon, label, onClick, done }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1.5 py-3 rounded-control border text-label uppercase transition-colors ${
        done ? 'border-good/40 bg-goodSoft text-good' : 'border-edge bg-panelHi text-textSoft hover:border-edgeHi'
      }`}
    >
      {done ? <CheckCircle2 size={18} /> : <Icon size={18} />}
      {label}
    </button>
  );
}

function WeekStrip({ programme, today }) {
  return (
    <Card>
      <SectionTitle action={<Link to="/train" className="text-body-sm text-accent">All weeks</Link>}>This week&rsquo;s sessions</SectionTitle>
      <ul className="space-y-2">
        {programme.weekSessions.map((s) => {
          const done = s.status === 'done' || s.status === 'shortened';
          return (
            <li key={s.slot}>
              <Link
                to={`/train/${programme.week}/${s.slot}`}
                className={`flex items-center gap-3 p-3 rounded-control border transition-colors ${
                  done ? 'border-good/40 bg-goodSoft' : s.date === today ? 'border-accent bg-accentSoft' : 'border-edge hover:border-edgeHi'
                }`}
              >
                <span className="text-label uppercase text-textMute w-10 shrink-0">{fmtDate(s.date, 'weekday-short')}</span>
                <span className="flex-1 text-body-sm font-medium">{s.name}</span>
                {done ? <CheckCircle2 size={18} className="text-good" /> : <ChevronRight size={18} className="text-textMute" />}
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ------------------------------------------------------------------ sheets

function LogSheets({ sheet, onClose, date, daily, onSaved, targets, onMigraine }) {
  const { show } = useToast();
  const [value, setValue] = useState('');
  const [extra, setExtra] = useState('');
  const [aside, setAside] = useState({});
  const [busy, setBusy] = useState(false);

  const open = (k) => sheet === k;

  async function save(payload, endpoint = '/logs/daily') {
    setBusy(true);
    try {
      await api.post(endpoint, { date, ...payload });
      show('Logged.');
      setValue('');
      setExtra('');
      setAside({});
      onSaved();
      onClose();
    } catch (err) {
      show(err.message, 'crit');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Sheet open={open('steps')} onClose={onClose} title="Steps today">
        <Field label="Steps" id="steps-in" hint={`Target is ${num(targets.step_target)}. Whatever the number, logging it is the useful bit.`}>
          <NumberInput id="steps-in" value={value} onChange={(e) => setValue(e.target.value)} placeholder={daily.steps ?? ''} autoFocus />
        </Field>
        <Button className="w-full" disabled={busy || !value} onClick={() => save({ steps: Number(value) })}>Save</Button>
      </Sheet>

      <Sheet open={open('protein')} onClose={onClose} title="Protein today">
        <Field label="Protein" id="prot-in" hint={`Target is ${targets.protein_g}g. An estimate is fine — 30g is a chicken breast, 20g is a scoop of protein, 6g is an egg.`}>
          <NumberInput id="prot-in" suffix="g" value={value} onChange={(e) => setValue(e.target.value)} placeholder={daily.protein_g ?? ''} autoFocus />
        </Field>
        <Field label="How did the eating go?" id="band">
          <Choice
            columns={3}
            value={extra}
            onChange={setExtra}
            options={[
              { value: 'under', label: 'Under' },
              { value: 'in', label: 'About right' },
              { value: 'over', label: 'Over' },
            ]}
          />
        </Field>
        <Button className="w-full" disabled={busy} onClick={() => save({ protein_g: value ? Number(value) : undefined, kcal_band: extra || undefined })}>Save</Button>
      </Sheet>

      <Sheet open={open('weight')} onClose={onClose} title="Weight">
        <Field label="Weight" id="w-in" hint="First thing, after the loo, before breakfast. It bounces around by a kilo or more day to day — the app reads the trend, not the reading.">
          <NumberInput id="w-in" suffix="kg" step="0.1" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
        </Field>
        {value && <p className="text-body-sm text-textSoft mb-4">That is {toStone(Number(value))}.</p>}
        <Button className="w-full" disabled={busy || !value} onClick={() => save({ kg: Number(value) }, '/logs/weight')}>Save</Button>
      </Sheet>

      <Sheet open={open('feel')} onClose={onClose} title="How are you today?">
        <Field label="Energy" id="en">
          <Choice
            columns={5}
            value={value}
            onChange={setValue}
            options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }))}
          />
        </Field>

        <Field label="Water" id="water" hint="Dehydration is one of your migraine triggers, so this is worth a rough note rather than nothing.">
          <Choice
            columns={4}
            value={aside.water_ml}
            onChange={(v) => setAside((a) => ({ ...a, water_ml: v }))}
            options={[
              { value: 750, label: 'Under 1L' },
              { value: 1250, label: '1–1.5L' },
              { value: 1750, label: '1.5–2L' },
              { value: 2250, label: '2L+' },
            ]}
          />
        </Field>

        {/* WHOOP stays a separate app by request — these are typed in on the
            days the numbers are worth having, and ignored otherwise. */}
        <div className="grid grid-cols-2 gap-3">
          <Field label="WHOOP recovery" id="wr">
            <NumberInput id="wr" suffix="%" value={aside.whoop_recovery ?? ''} onChange={(e) => setAside((a) => ({ ...a, whoop_recovery: e.target.value }))} />
          </Field>
          <Field label="Sleep" id="ws">
            <NumberInput id="ws" suffix="h" step="0.25" value={aside.whoop_sleep_h ?? ''} onChange={(e) => setAside((a) => ({ ...a, whoop_sleep_h: e.target.value }))} />
          </Field>
        </div>

        <Field label="Anything worth noting?" id="note" hint="Migraine, poor sleep, a big day out — all of it changes what the app should expect of you.">
          <TextArea id="note" value={extra} onChange={(e) => setExtra(e.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {/* A migraine gets its own proper form — severity and timing are what
              make the pattern analysis possible later. */}
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              await save({ ...numeric(aside), energy: value || undefined, note: extra || undefined });
              onMigraine();
            }}
            disabled={busy}
          >
            Migraine today
          </Button>
          <Button variant="secondary" size="sm" onClick={() => save({ ...numeric(aside), ate_out: 1, note: extra || null, energy: value || undefined })} disabled={busy}>
            Ate out today
          </Button>
        </div>
        <Button className="w-full" disabled={busy} onClick={() => save({ ...numeric(aside), energy: value || undefined, note: extra || undefined })}>Save</Button>
      </Sheet>
    </>
  );
}

// Blank boxes should stay blank rather than arriving as 0.
function numeric(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v == null) continue;
    out[k] = Number(v);
  }
  return out;
}
